import { ScannerScheduledDowntimeAPI } from '@API/data-services/scanner-scheduled-downtime-service'
import { ResponseError, ScheduledDowntime } from '@Generated/back-office-client/src'
import { AlertDialog, Button, Flex, Text } from '@radix-ui/themes'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

type Props = {
  target: ScheduledDowntime | null
  onClose: () => void
}

export const DeleteConfirm = ({ target, onClose }: Props) => {
  const queryClient = useQueryClient()
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (target) {
      setSubmitError(null)
    }
  }, [target])

  const mutation = useMutation({
    mutationFn: (id: string) => ScannerScheduledDowntimeAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scanner-scheduled-downtime'] })
      onClose()
    },
    onError: async (err: unknown) => {
      if (err instanceof ResponseError) {
        try {
          const body = await err.response.json()
          setSubmitError(body?.message ?? err.message)
        } catch {
          setSubmitError(err.message)
        }
      } else {
        setSubmitError('An unexpected error occurred.')
      }
    },
  })

  return (
    <AlertDialog.Root open={!!target} onOpenChange={(open) => (!open ? onClose() : null)}>
      <AlertDialog.Content maxWidth="450px">
        <AlertDialog.Title>Delete Downtime Window</AlertDialog.Title>
        <AlertDialog.Description size="2">
          Are you sure you want to delete this downtime window? This action cannot be undone.
        </AlertDialog.Description>
        {target?.reason ? (
          <Text as="p" size="2" mt="2" color="gray">
            Reason: {target.reason}
          </Text>
        ) : null}

        {submitError ? (
          <Text as="p" size="2" mt="2" color="red">
            {submitError}
          </Text>
        ) : null}

        <Flex gap="3" mt="4" justify="end">
          <AlertDialog.Cancel>
            <Button variant="soft" color="gray">
              Cancel
            </Button>
          </AlertDialog.Cancel>
          <Button
            variant="solid"
            color="red"
            loading={mutation.isPending}
            onClick={() => target && mutation.mutate(target.id)}>
            Delete
          </Button>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  )
}
